import { Injectable, Injector, runInInjectionContext } from '@angular/core';
import { Storage, ref, uploadBytes, getDownloadURL, listAll, deleteObject } from '@angular/fire/storage';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FirebaseStorageService {
  private readonly logoFileNames = [
    'upsaclay_logo.png',
    'lunds_logo.png',
    'lmu_logo.png',
    'porto_logo.png',
    'szte_logo.jpg',
    'alcala_logo.jpg',
    'hamburg_logo.png',
    'novisad_logo.jpg',
    'uit_logo.png',
    'EUGLOH_logo.png'
  ];

  async getLogoUrls(): Promise<Map<string, string>> {
    const logoUrls = new Map<string, string>();

    for (const filename of this.logoFileNames) {
      try {
        const url = await runInInjectionContext(this.injector, () => {
          const storageRef = ref(this.storage, `images/logos/${filename}`);
          return getDownloadURL(storageRef);
        });
        logoUrls.set(filename, url);
      } catch (error) {
        console.error(`Error getting logo URL for ${filename}:`, error);
        logoUrls.set(filename, '');
      }
    }
    return logoUrls;
  }

  constructor(private storage: Storage, private injector: Injector) { }

  async uploadApplicationDocument(applicantId: string, file: File, documentType: string): Promise<{ name: string, url: string }> {
    return await runInInjectionContext(this.injector, async () => {
      try {
        const applicantsRef = ref(this.storage, `Applicants/${applicantId}`);

        try {
          await listAll(applicantsRef);
        } catch (error) {
          console.error('Applicants folder will be created for:', applicantId);
        }

        const fileExtension = file.name.split('.').pop() || 'jpg';
        const path = `Applicants/${applicantId}/${documentType}.${fileExtension}`;

        const storageRef = ref(this.storage, path);

        await uploadBytes(storageRef, file);

        return {
          name: `${documentType}.${fileExtension}`,
          url: path
        };
      } catch (error) {
        console.error('Error uploading application document:', error);
        throw error;
      }
    });
  }

  async deleteApplicationDocument(applicantId: string, fileName: string): Promise<void> {
    await runInInjectionContext(this.injector, () => {
      const path = `applicants/${applicantId}/${fileName}`;
      const fileRef = ref(this.storage, path);
      return deleteObject(fileRef);
    });
  }

  async deleteApplicantFolder(applicantId: string): Promise<void> {
    try {
      const folderPath = `Applicants/${applicantId}`;
      const folderRef = ref(this.storage, folderPath);

      // List all files in the folder
      const listResult = await listAll(folderRef);

      // Delete each file in the folder
      const deletePromises = listResult.items.map(item => deleteObject(item));
      await Promise.all(deletePromises);

    } catch (error) {
      console.error('Error deleting applicant folder:', error);
      throw error;
    }
  }

  async getApplicationDocumentUrl(applicantId: string, fileName: string): Promise<string | null> {
    try {
      return await runInInjectionContext(this.injector, async () => {
        const path = `applicants/${applicantId}/${fileName}`;
        const fileRef = ref(this.storage, path);
        return await getDownloadURL(fileRef);
      });
    } catch (error) {
      console.error('Error getting application document URL:', error);
      return null;
    }
  }

  async getImageUrl(path: string): Promise<string> {
    return runInInjectionContext(this.injector, async () => {
      try {
        const folderPath = path.substring(0, path.lastIndexOf('/'));
        const fileName = path.substring(path.lastIndexOf('/') + 1);

        const listResult = await listAll(ref(this.storage, folderPath));
        const fileExists = listResult.items.some(item => item.name === fileName);

        if (!fileExists) return '';

        const storageRef = ref(this.storage, path);
        return await getDownloadURL(storageRef);
      } catch (error: any) {
        if (error?.code === 'storage/object-not-found') return '';
        console.error('Error fetching image:', error);
        return '';
      }
    });
  }

  async checkAndCreateUsersFolder(): Promise<void> {
    const usersRef = ref(this.storage, 'users');
    try {
      await listAll(usersRef);
    } catch (error) {
      console.error('Users folder will be created on first upload');
    }
  }

  async uploadCV(userId: string, file: File): Promise<string> {
    try {
      await this.deleteCV(userId);
      const path = `users/cv/${userId}.pdf`;

      return await runInInjectionContext(this.injector, async () => {
        const storageRef = ref(this.storage, path);
        await uploadBytes(storageRef, file);
        return await this.getImageUrl(path);
      });
    } catch (error) {
      console.error('Error uploading CV:', error);
      throw error;
    }
  }

  async deleteCV(userId: string): Promise<void> {
    await runInInjectionContext(this.injector, async () => {
      const path = `users/cv/${userId}.pdf`;
      const fileRef = ref(this.storage, path);
      try {
        await deleteObject(fileRef);
      } catch (error: any) {
        if (error.code !== 'storage/object-not-found') throw error;
      }
    });
  }

  async checkIfCvExists(userId: string): Promise<boolean> {
    try {
      const listResult = await runInInjectionContext(this.injector, () =>
        listAll(ref(this.storage, 'users/cv'))
      );
      return listResult.items.some(item => item.name === `${userId}.pdf`);
    } catch (error) {
      return false;
    }
  }

  async getUserCV(userId: string): Promise<string | null> {
    return await runInInjectionContext(this.injector, async () => {
      const path = `users/cv/${userId}.pdf`;
      try {
        const folderRef = ref(this.storage, 'users/cv');
        const listResult = await listAll(folderRef);

        const cvExists = listResult.items.some(item => item.name === `${userId}.pdf`);
        if (!cvExists) return null;

        const fileRef = ref(this.storage, path);
        return await runInInjectionContext(this.injector, () => getDownloadURL(fileRef));
      } catch (error: any) {
        console.error('Error getting CV:', error);
        return null;
      }
    });
  }

  async getApplicantDocuments(applicantId: string): Promise<Array<{ type: string, url: string }>> {
    try {
      const folderPath = `Applicants/${applicantId}`;

      const listResult = await runInInjectionContext(this.injector, () => {
        const folderRef = ref(this.storage, folderPath);
        return listAll(folderRef);
      });

      if (listResult.items.length === 0) return [];

      const documents: Array<{ type: string, url: string }> = [];

      for (const item of listResult.items) {
        try {
          const downloadUrl = await runInInjectionContext(this.injector, () => getDownloadURL(item));

          const fileName = item.name;
          const fileNameParts = fileName.split('.');
          const fileType = fileNameParts.length > 1 ? fileNameParts.slice(0, -1).join('.') : fileName;

          documents.push({ type: fileType, url: downloadUrl });
        } catch (error) {
          console.error(`Error getting download URL for document: ${item.name}`, error);
        }
      }
      return documents;
    } catch (error) {
      console.error('Error getting applicant documents:', error);
      return [];
    }
  }
}
